import { useModal } from "@racoon/ui";
import { useT, Namespace } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { MemoryRouter } from "react-router-dom";
import { AssignRouteContent } from "../ui/assign-route-content";
import { AssignRouteHeader } from "../ui/assign-route-header";
import { createMapVisibility } from "../model/map-visibility";
import { DraftJob } from "../model/draft-jobs";

/** A pickup location group's stops on a day: existing jobs and drafts. */
export interface GroupStops {
  jobs: ConnectProto.Job[];
  drafts: DraftJob[];
}

export interface OpenAssignRouteOptions {
  preselectedTruckId?: string;
  /**
   * Jobs the first trip opens with — e.g. the rows checked in the orders table.
   * The default truck is then picked among those that can actually serve them.
   */
  preselectedJobs?: ConnectProto.Job[];
  /**
   * Jobs that do not exist yet — a pickup location group expanded into the jobs
   * it would produce. They join the first trip like real ones (reorderable,
   * movable between trips, droppable) and are created on submit, right before
   * the routes that carry them.
   */
  draftJobs?: DraftJob[];
  /**
   * The pickup location group the route is built out of. Its trips are then
   * created by the group's own endpoint, drafts and existing jobs together.
   */
  pickupLocationGroupId?: bigint;
  /**
   * Re-reads the group's stops for another day. Picking a date in the modal
   * replaces the trips with that day's stops.
   */
  loadGroupStops?: (date: Date) => Promise<GroupStops>;
  /**
   * The route follows the group's template: a stop per link of the group. Bulk
   * create reuses a window's own unrouted job and creates the rest, duplicates
   * of routed or other groups' jobs included, without asking.
   */
  groupTemplate?: boolean;
  /**
   * The day the route is planned for, today when omitted. Draft jobs built for
   * a given day (a pickup location group's single-day view) open on that day,
   * since the new jobs are stamped with it.
   */
  initialDate?: Date;
  /** Called once the routes are created. */
  onCreated?: () => void;
}

export function useAssignRouteModal() {
  const { openModal } = useModal();
  const t = useT();

  const openAssignRoute = (options: OpenAssignRouteOptions = {}) => {
    // Shared by the header's toggle and the body's map panel — see map-visibility.
    const mapVisibility = createMapVisibility();

    return openModal({
      size: "custom",
      width: { lg: "80svw" },
      height: { lg: "80svh" },
      title: (
        <AssignRouteHeader
          title={t(Namespace.Infopanel, "modals.assignRoute.title")}
          mapVisibility={mapVisibility}
        />
      ),
      closeButtonTop: "top-5",
      // `[&>div]:w-full` stretches the title wrapper ModalProvider adds so the
      // header row can push the map toggle to the right; `pr-12` keeps it clear
      // of the absolutely positioned close button.
      headerClassName: "!p-4 !pr-12 [&>div]:w-full",
      // `h-full` would make the body as tall as the whole modal and push the
      // footer past its bottom edge — it has to take the space left by the header.
      bodyClassName: "!p-0 min-h-0 flex-1",
      // ModalProvider lives above the app router, so the modal body has no
      // router context — the map's useInfoPanel needs one. A MemoryRouter also
      // keeps the dashboard's ?infoType/?infoId selection out of the preview
      // (same approach as useRouteJobsModal).
      body: (
        <MemoryRouter>
          <AssignRouteContent
            preselectedTruckId={options.preselectedTruckId}
            preselectedJobs={options.preselectedJobs}
            draftJobs={options.draftJobs}
            pickupLocationGroupId={options.pickupLocationGroupId}
            loadGroupStops={options.loadGroupStops}
            groupTemplate={options.groupTemplate}
            initialDate={options.initialDate}
            onCreated={options.onCreated}
            mapVisibility={mapVisibility}
          />
        </MemoryRouter>
      ),
    });
  };

  return { openAssignRoute };
}
